import {
  MaintenanceImpact,
  MaintenanceMode,
  MaintenanceNotice,
  MaintenanceState,
  MaintenanceStatus,
} from './types';

const STATES: readonly string[] = ['active', 'upcoming'];
const MODES: readonly string[] = ['block', 'banner'];
const IMPACTS: readonly string[] = ['low', 'medium', 'high'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isValidDate = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

/** Parses one notice, or returns null when it is unusable (it is then dropped). */
export function parseNotice(raw: unknown): MaintenanceNotice | null {
  if (!isRecord(raw)) return null;
  const { state, mode, area, impact, title, message, startsAt, endsAt } = raw;

  if (typeof state !== 'string' || !STATES.includes(state)) return null;
  if (typeof mode !== 'string' || !MODES.includes(mode)) return null;
  if (typeof title !== 'string' || typeof message !== 'string') return null;
  if (!isValidDate(startsAt)) return null;
  if (area !== null && area !== undefined && typeof area !== 'string') return null;

  return {
    state: state as MaintenanceState,
    mode: mode as MaintenanceMode,
    area: typeof area === 'string' && area.length > 0 ? area : null,
    impact:
      typeof impact === 'string' && IMPACTS.includes(impact)
        ? (impact as MaintenanceImpact)
        : 'medium',
    title,
    message,
    startsAt,
    endsAt: isValidDate(endsAt) ? endsAt : null,
  };
}

/**
 * Parses the `data` payload of GET /maintenance. Returns null when the payload
 * is unusable as a whole, so callers can fail open (show nothing).
 */
export function parseMaintenanceStatus(raw: unknown): MaintenanceStatus | null {
  if (!isRecord(raw) || !Array.isArray(raw.notices)) return null;

  const notices = (raw.notices as unknown[])
    .map((item) => parseNotice(item))
    .filter((item): item is MaintenanceNotice => item !== null);

  return { blocking: raw.blocking === true, notices };
}
